import Box from "@mui/material/Box";
import type {ColumnDef} from "@tanstack/react-table";
import type {MappingCode} from "crqlar-react-common/dist/models/mappingCodes/mappingCode";
import {MappingCodeService} from "crqlar-react-common/dist/services/mappings/mappingCodes.service";
import {useTrackScreenVisit} from "crqlar-react-common/dist/utils/analytics.util";
import {ellpisize} from "crqlar-react-common/dist/utils/text.util";
import {useMemo} from "react";
import {useTranslation} from "react-i18next";
import {useNavigate} from "react-router-dom";
import {
    ServerTable,
    ServerTableColumns,
    ServerTableCreate,
    ServerTableFrame,
    ServerTableFullscreen,
    ServerTableProvider,
    ServerTableSearch,
    ServerTableSort,
    ServerTableToolbar,
    useServerTable
} from "../../common/serverTable";
import SettingsListPage from "../layout/SettingsListPage";

const MappingCodesList = () => {
    useTrackScreenVisit('system mappings codes');

    const navigate = useNavigate();
    const {t} = useTranslation();
    const columns = useMemo<ColumnDef<MappingCode>[]>(() => [
        {
            id: 'code',
            header: t('Code'),
            accessorFn: mappingCode => mappingCode.code,
            cell: ({row}) => <Box>{row.original.code}</Box>,
            size: 160,
            meta: {defaultActive: true, isServerSortable: true}
        },
        {
            id: 'type',
            header: t('Type'),
            accessorFn: mappingCode => mappingCode.type,
            cell: ({row}) => <Box>{row.original.type || ''}</Box>,
            size: 260,
            meta: {defaultActive: true, isServerSortable: true}
        },
        {
            id: 'description',
            header: t('Description'),
            accessorFn: mappingCode => mappingCode.description,
            cell: ({row}) => <Box>{ellpisize(row.original.description || '', 80)}</Box>,
            size: 320,
            meta: {defaultActive: true, isServerSortable: true, grow: true}
        },
        {
            id: 'id',
            header: t('ID'),
            accessorFn: mappingCode => mappingCode.id,
            cell: ({row}) => <Box>{row.original.id || ''}</Box>,
            size: 260,
            meta: {defaultActive: false, isServerSortable: true}
        }
    ], [t]);
    const controller = useServerTable({
        tableId: 'system-mapping-codes',
        columns,
        getRowId: mappingCode => mappingCode.id!,
        onRowClick: mappingCode => navigate(`/system/mappingCodes/${mappingCode.id}`),
        initialSorting: [{id: 'id', desc: true}],
        queryFn: async ({pagination, searchQuery, sorting}) => {
            const page = await MappingCodeService.list({
                name: searchQuery ? [searchQuery] : null,
                limit: {
                    offset: pagination.pageIndex * pagination.pageSize,
                    limit: pagination.pageSize,
                    sort: sorting ? [sorting] : undefined
                }
            });

            return {
                content: page.content ?? [],
                totalElements: page.totalElements ?? 0
            };
        }
    });

    return (
        <SettingsListPage title={t('Mapping codes')} back={() => navigate('/system')}>
            <ServerTableProvider controller={controller}>
                <ServerTableFrame>
                    <ServerTableToolbar hoistOnCompact>
                        <ServerTableSearch/>
                        <ServerTableSort/>
                        <ServerTableFullscreen/>

                        <ServerTableColumns/>

                        <ServerTableCreate
                            label={t('Create Mapping Code')}
                            onClick={() => navigate('/system/mappingCodes/new')}
                        />
                    </ServerTableToolbar>

                    <ServerTable/>
                </ServerTableFrame>
            </ServerTableProvider>
        </SettingsListPage>
    );
};

export default MappingCodesList;
